import { useState, useCallback, useEffect } from 'react';
import { FormFieldValue } from '@/features/submission_forms/types';
import { API } from '@/utils/API';

interface UseSubmissionAnswersReturn {
  answers: Record<string, FormFieldValue>;
  isLoading: boolean;
  error: string | null;
}

export const useSubmissionAnswers = (
  submissionId: string
): UseSubmissionAnswersReturn => {
  const [answers, setAnswers] = useState<Record<string, FormFieldValue>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnswers = useCallback(async () => {
    if (!submissionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/submission_answers`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch answers');
      }

      const data = await response.json();
      setAnswers(data.answers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch answers');
    } finally {
      setIsLoading(false);
    }
  }, [submissionId]);

  // Auto-fetch answers when submissionId changes
  useEffect(() => {
    if (submissionId) {
      fetchAnswers();
    }
  }, [submissionId, fetchAnswers]);

  return {
    answers,
    isLoading,
    error,
  };
};
